// src/components/ImageStyleAnalyzer.jsx
import React, { useState } from 'react';
import { analyzeStyleFromImage } from '../utils/analyzeStyleFromImage';

const ImageStyleAnalyzer = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [styleAdvice, setStyleAdvice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous states
    setError('');
    setStyleAdvice('');

    // Validate file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file size must be less than 10MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Image = reader.result;
      setPreviewURL(base64Image);
      setLoading(true);
      
      try {
        const advice = await analyzeStyleFromImage(base64Image);
        setStyleAdvice(advice);
        setError('');
      } catch (err) {
        console.error('Style analysis error:', err);
        setError('Failed to analyze style. Please check your API key and try again.');
        setStyleAdvice('');
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read the image file');
      setLoading(false);
    };

    reader.readAsDataURL(file);
    setUploadedImage(file);
  };

  const clearImage = () => {
    setUploadedImage(null);
    setPreviewURL(null);
    setStyleAdvice('');
    setError('');
    setLoading(false);
  };

  return (
    <div className="bg-white shadow-lg border border-stone-200 p-6 rounded-md max-w-xl mx-auto mt-12">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Style Analysis from Photo</h2>
      
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="block w-full mb-4 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {previewURL && (
        <div className="relative mb-4">
          <img
            src={previewURL}
            alt="Preview"
            className="w-full max-w-xs mx-auto rounded shadow"
          />
          <button
            onClick={clearImage}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
            title="Remove image"
          >
            ×
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center mb-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-700 mr-3"></div>
          <p className="text-amber-700">Analyzing your style with Claude...</p>
        </div>
      )}

      {styleAdvice && !loading && (
        <div className="mt-6 text-sm text-gray-800 whitespace-pre-line border-t pt-4">
          <h3 className="text-lg font-medium mb-3 text-gray-900">Your Personal Style Recommendations</h3>
          <div className="prose prose-sm max-w-none">
            {styleAdvice}
          </div>
        </div>
      )}

      {!previewURL && !loading && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="mt-2">Upload a photo to get personalized style advice</p>
        </div>
      )}
    </div>
  );
};

export default ImageStyleAnalyzer;